import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as activityService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";

const Activities = () => {
  const queryClient = useQueryClient();

  // State management
  const [selectedActivity, setSelectedActivity] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch } = useTableSearch();
  const [activityName, setActivityName] = useState("");
  const [activityStatus, setActivityStatus] = useState(1);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [nameError, setNameError] = useState("");

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: activitiesData } = useQuery({
    queryKey: ["activities", currentPage, activeSearch],
    queryFn: () =>
      activityService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: activityService.create,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Activity added successfully!");
      setAddDialogOpen(false);
      setActivityName("");
      queryClient.invalidateQueries(["activities"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.response?.data?.message || "Failed to add activity");
    },
  });

  const updateMutation = useMutation({
    mutationFn: activityService.update,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Activity updated successfully!");
      setEditDialogOpen(false);
      setActivityName("");
      queryClient.invalidateQueries(["activities"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.response?.data?.message || "Failed to update activity");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: activityService.deleteActivity,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Activity deleted successfully!");
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["activities"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.response?.data?.message || "Failed to delete activity");
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: activityService.getById,
    onSuccess: (data) => {
      setSelectedActivity(data.data);
      setActivityName(data.data.name);
      setActivityStatus(data.data.status);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      showErrorMessage(error.response?.data?.message || "Failed to fetch activity details");
    },
  });

  // Helper functions
  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setErrorMessage(errorMsg);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  // Action handlers
  const handleAddClick = () => {
    setActivityName("");
    setActivityStatus(1);
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (activity) => {
    setSelectedActivity(activity);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (activityId) => {
    getByIdMutation.mutate(activityId);
  };

  const handleSaveActivity = () => {
    setIsLoading(true);
    if (!activityName.trim()) {
      setIsLoading(false);
      setNameError("Activity name is required");
      return;
    }

    createMutation.mutate({
      name: activityName,
      status: Number(activityStatus),
    });
  };

  const handleUpdateActivity = () => {
    setIsLoading(true);
    if (!activityName.trim()) {
      setIsLoading(false);
      setNameError("Activity name is required");
      return;
    }

    if (selectedActivity) {
      updateMutation.mutate({
        id: selectedActivity.id,
        activity: { name: activityName, status: Number(activityStatus) },
      });
    }
  };

  const handleDeleteActivity = () => {
    setIsLoading(true);
    if (selectedActivity) {
      deleteMutation.mutate(selectedActivity.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const activities = activitiesData?.data?.data || [];

  const pagination = {
    currentPage: activitiesData?.data?.current_page || 1,
    lastPage: activitiesData?.data?.last_page || 1,
    perPage: activitiesData?.data?.per_page || 10,
    total: activitiesData?.data?.total || 0,
    from: activitiesData?.data?.from || 0,
    to: activitiesData?.data?.to || 0,
  };

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.activities.title}
        subtitle={ADMIN_PAGE_META.activities.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search activities..."
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border transition-all"
            >
              Add Activities
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Activities</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {activities.length > 0 ? (
            activities.map((activity, index) => (
              <AdminTableRow key={activity.id}>
                <AdminTableCell>{index + 1}</AdminTableCell>
                <AdminTableCell>{activity.name}</AdminTableCell>
                <AdminTableCell>
                  <StatusBadge variant={getStatusVariant(activity.status)}>
                    {activity.status === 1 ? "Active" : "In-Active"}
                  </StatusBadge>
                </AdminTableCell>
                <TableActionsCell>
                  <EditActionButton
                    showLabel={false}
                    onClick={() => handleEditClick(activity.id)}
                  />
                  <DeleteActionButton
                    showLabel={false}
                    onClick={() => handleDeleteClick(activity)}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow colSpan={4} emptyMessage="No activities found" />
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {/* Add Activities Dialog */}
      <Dialog
        open={addDialogOpen}
        isLoading={isLoading}
        onClose={() => {
          setAddDialogOpen(false);
          setNameError("");
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Activities
          </div>
        }
        primaryButtonText="Add Activities"
        primaryButtonAction={handleSaveActivity}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setNameError("");
        }}
        width="w-[480px]"
      >
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_132px] gap-4 px-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activity Name
            </label>
            <input
              type="text"
              className={`text-sm w-full h-10 px-3 border ${
                nameError ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
              placeholder="Enter activity name"
              value={activityName}
              onChange={(e) => {
                setActivityName(e.target.value);
                if (e.target.value.trim()) {
                  setNameError("");
                }
              }}
            />
            {nameError && (
              <p className="text-red-500 text-xs mt-1">{nameError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={activityStatus}
              onChange={(e) => setActivityStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>In-Active</option>
            </select>
          </div>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        isLoading={isLoading}
        onClose={() => {
          setEditDialogOpen(false);
          setNameError("");
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Activity
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateActivity}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setEditDialogOpen(false);
          setNameError("");
        }}
        width="w-[480px]"
      >
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_132px] gap-4 px-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activity Name
            </label>
            <input
              type="text"
              className={`text-sm w-full h-10 px-3 border ${
                nameError ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
              placeholder="Enter activity name"
              value={activityName}
              onChange={(e) => {
                setActivityName(e.target.value);
                if (e.target.value.trim()) {
                  setNameError("");
                }
              }}
            />
            {nameError && (
              <p className="text-red-500 text-xs mt-1">{nameError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={activityStatus}
              onChange={(e) => setActivityStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>In-Active</option>
            </select>
          </div>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Activity
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteActivity}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this activity? This action cannot be
            undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default Activities;
